import { useEffect, useState } from 'react';
import { onAuthStateChanged, signInAnonymously, type User } from 'firebase/auth';
import { httpsCallable, type FunctionsError } from 'firebase/functions';
import { auth, functions } from '../services/firebase';

type Status =
  | { phase: 'loading' }
  | { phase: 'ready'; user: User }
  | { phase: 'denied'; reason: 'ip' | 'config' | 'unknown'; message: string };

const setAdminRoleFn = httpsCallable<void, { ok: true }>(functions, 'setAdminRole');

async function ensureAdminClaim(user: User): Promise<void> {
  // First check whether the user already carries the claim (from a previous visit).
  const existing = await user.getIdTokenResult();
  if (existing.claims.admin === true) return;

  // Ask the server. If the IP gate passes, the claim is set on the server side;
  // we then force-refresh the token so the new claim ships with subsequent calls.
  await setAdminRoleFn();
  await user.getIdToken(true);
}

function classifyError(err: unknown): { reason: 'ip' | 'config' | 'unknown'; message: string } {
  const e = err as Partial<FunctionsError> & { message?: string };
  if (e?.code === 'functions/permission-denied') {
    return { reason: 'ip', message: '社内ネットワークからアクセスしてください。' };
  }
  if (e?.code === 'functions/failed-precondition') {
    return { reason: 'config', message: 'IP許可リストが設定されていません。管理者に連絡してください。' };
  }
  return { reason: 'unknown', message: e?.message ?? '認可エラーが発生しました。' };
}

/**
 * Drives the dashboard's auth flow:
 *   1. signInAnonymously on first load (or restore the existing anon user)
 *   2. call setAdminRole — server checks the request IP and grants admin: true
 *   3. force-refresh the ID token so the claim is visible to Firestore/Storage
 *
 * Errors land in the `denied` phase with a hint about what to fix.
 */
export function useAuth(): Status {
  const [status, setStatus] = useState<Status>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (!user) {
          await signInAnonymously(auth);
          return; // onAuthStateChanged will re-fire with the new user
        }
        await ensureAdminClaim(user);
        if (!cancelled) setStatus({ phase: 'ready', user });
      } catch (err) {
        const cls = classifyError(err);
        if (!cancelled) setStatus({ phase: 'denied', ...cls });
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return status;
}
